import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUserRoles } from '../../features/auth/authSlice';
import { selectClaimDocuments } from '../../features/documents/documentSlice';
import { getRoleBasedClaimsRoute } from '../../utils/navigationUtils';
import OmCard from '../ui/OmCard';
import OmButton from '../ui/OmButton';

const ActionsPanel = ({ userRoles, onDocumentUpload, claim, onRecommend, onApprove, onReview, onRequestMissingDocs }) => {
  const navigate = useNavigate();
  const currentUserRoles = useSelector(selectUserRoles);
  const hasUploadRole = userRoles.includes('HR') || userRoles.includes('Broker') || userRoles.includes('CRM') || userRoles.includes('Admin');
  
  // Check if claim status allows document uploads
  const finalizedStatuses = ['APPROVED', 'REJECTED', 'PAID', 'CLOSED'];
  const isClaimFinalized = finalizedStatuses.includes(claim?.status);
  const canUpload = hasUploadRole && !isClaimFinalized;

  // Helper function to determine what workflow actions are available
  const getAvailableWorkflowActions = () => {
    const actions = [];
    const currentStatus = claim?.status;

    // Claims Officer actions
    if (userRoles.includes('Claims_Officer')) {
      if (currentStatus === 'UNDER_REVIEW' || currentStatus === 'BENEFIT_PENDING_APPROVAL') {
        actions.push({
          key: 'recommend',
          label: 'Recommend for Approval',
          icon: 'check',
          buttonType: 'filled-button',
          onClick: onRecommend,
          priority: 1
        });

        // Allow requesting missing documents directly from processing statuses
        actions.push({
          key: 'request-missing-docs',
          label: 'Request Missing Documents',
          icon: 'inventory_2',
          buttonType: 'outlined-button',
          onClick: onRequestMissingDocs,
          priority: 2
        });
      }
    }

    // Approver actions
    if (userRoles.includes('Approver')) {
      if (currentStatus === 'RECOMMENDED_FOR_APPROVAL' || currentStatus === 'BENEFIT_PENDING_APPROVAL') {
        actions.push({
          key: 'approve',
          label: 'Make Decision',
          icon: 'judge',
          buttonType: 'filled-button',
          onClick: onApprove,
          priority: 1
        });
      }
    }

    // Sort actions by priority (higher priority first)
    return actions.sort((a, b) => (a.priority || 999) - (b.priority || 999));
  };

  const workflowActions = getAvailableWorkflowActions();

  return (
    <OmCard className="bg-crystal-white shadow-soft border border-steel/20 rounded-lg overflow-hidden transition-all duration-300 ease-in-out hover:shadow-lg hover:-translate-y-1 hover:border-fresh-leaf/30 focus-within:ring-2 focus-within:ring-fresh-leaf/25 focus-within:border-fresh-leaf/40">
      <div className="px-6 sm:px-8 pt-6 sm:pt-8 pb-4 sm:pb-6 border-b border-steel/15">
        <h3 className="text-xl sm:text-2xl font-semibold text-graphite mb-3">Actions</h3>
        <p className="text-sm sm:text-base text-steel">Quick actions for this claim</p>
      </div>
      <div className="p-6 sm:p-8">
        <div className="space-y-4 sm:space-y-5">
          {/* Workflow action buttons */}
          {workflowActions.map(action => (
            <OmButton
              key={action.key}
              onClick={action.onClick}
              buttonType={action.buttonType}
              buttonSize="large"
              leadingIcon={action.icon}
              className="w-full"
            >
              {action.label}
            </OmButton>
          ))}
          
          <OmButton
            onClick={() => navigate(getRoleBasedClaimsRoute(currentUserRoles))}
            buttonType="outlined-button"
            buttonSize="large"
            leadingIcon="arrow_back"
            className="w-full"
          >
            Back to Claims List
          </OmButton>
          
          {hasUploadRole && (
            <OmButton
              onClick={canUpload ? onDocumentUpload : undefined}
              buttonType="filled-button"
              buttonSize="large"
              leadingIcon="upload"
              className={`w-full ${!canUpload ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!canUpload}
              title={!canUpload ? `Cannot upload documents - claim is ${claim?.status}` : 'Upload documents'}
            >
              Upload Documents
            </OmButton>
          )}
        </div>
      </div>
    </OmCard>
  );
};

export default ActionsPanel;
